import { useCallback, useEffect, useRef, useState } from 'react';

const useBuySellAccountSearch = ({ open, onSearch }) => {
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const onSearchRef = useRef(onSearch);
  const searchRequestRef = useRef(0);

  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  const resetSearch = useCallback(() => {
    searchRequestRef.current += 1;
    setInputValue('');
    setSearchQuery('');
    setSearchOptions([]);
    setSearchLoading(false);
    setSelectedAccount(null);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const trimmed = searchQuery.trim();
    const searchFn = onSearchRef.current;
    if (!trimmed || typeof searchFn !== 'function') {
      searchRequestRef.current += 1;
      setSearchLoading(false);
      setSearchOptions([]);
      return undefined;
    }

    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    const handle = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await searchFn(trimmed);
        if (searchRequestRef.current !== requestId) return;
        setSearchOptions(result || []);
      } finally {
        if (searchRequestRef.current === requestId) {
          setSearchLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(handle);
  }, [open, searchQuery]);

  const handleAccountChange = useCallback((next) => {
    setSelectedAccount(next);
    if (next) {
      setSearchQuery('');
      setSearchOptions([]);
    }
  }, []);

  return {
    inputValue,
    searchOptions,
    searchLoading,
    selectedAccount,
    setInputValue,
    setSearchQuery,
    setSelectedAccount,
    handleAccountChange,
    resetSearch,
  };
};

export default useBuySellAccountSearch;
